export type Categorie = 'bebe' | 'enfant' | 'adolescent' | 'adulte'

export interface Compte { id: number; nom: string; rang: number }
export interface Personne {
  id: number; compte_id: number; prenom: string; categorie: Categorie;
  actif: boolean; ordre: number; avatar_version: number
}
export interface Parametre {
  annee: number; total_nuitees: number; date_debut: string; date_fin: string;
  hero_position: number;
}
export interface Nuit { id: number; date_debut: string; date_fin: string; libelle: string; ordre: number }
export interface Repas { id: number; date: string; type: 'M' | 'S'; ordre: number }
export interface Depense {
  id: number; date: string; compte_id: number; montant: number; libelle: string
}

export interface PresenceFlat { personne_id: number; nuit_id?: number; repas_id?: number; present: boolean }

export interface NuitsResp {
  nuits: Nuit[];
  /** 4 états encodés (frontend reconstruit absent=vide à partir de l'absence de ligne) :
   *   - present=T, tentatif=F, refus=F → confirmé
   *   - present=T, tentatif=T, refus=F → provisoire
   *   - present=F, refus=T             → ne vient pas */
  presences: { personne_id: number; nuit_id: number; present: boolean; tentatif: boolean; refus: boolean }[]
}
export interface RepasResp { repas: Repas[]; presences: { personne_id: number; repas_id: number; present: boolean }[] }

export interface LigneNuitPersonne {
  personne_id: number; prenom: string; compte_id: number; categorie: Categorie;
  coefficient: number; nb_nuits: number; poids: number; montant_du: number;
  nb_nuits_tentatifs: number; montant_du_max: number;
}
export interface LigneNuitCompte {
  compte_id: number; compte_nom: string;
  nb_personnes_presentes: number; nb_nuits_total: number; poids_total: number; montant_du: number;
  nb_nuits_tentatifs_total: number; montant_du_max: number;
}
export interface LigneDetailSemainePersonne {
  personne_id: number;
  prenom: string;
  compte_id: number;
  compte_nom: string;
  categorie: Categorie;
  coefficient: number;
  nb_nuits: number;
  montant_base: number;         // nb_nuits × base_rate (plein tarif)
  montant_apres_coeff: number;  // coefficient × montant_base
  part_redistribuee: number;    // report du reliquat enfants/bébés (adultes/ados)
  montant_final: number;
}
export interface LigneFamilleSemaine {
  compte_id: number;
  compte_nom: string;
  nb_nuits: number;
  cout_brut: number;   // colonne E « Coût brut famille »
  cout_net: number;    // colonne K « Coût Net famille »
}
export interface SemaineNuitees {
  index: number;
  libelle: string;
  date_debut: string;         // ISO YYYY-MM-DD
  date_fin: string;           // ISO YYYY-MM-DD
  nb_nuits: number;
  cout: number;               // 1 000 € typiquement
  nb_nuitees_adulte_ado: number;
  nb_nuitees_enfant: number;
  nb_nuitees_bebe: number;
  prix_par_poids: number;     // = prix_nuit_adulte (conservé pour compat)
  poids_total: number;
  nb_nuitees_total: number;
  base_rate: number;          // cout / nb_nuitees_total
  montant_redistribue: number;
  prix_nuit_adulte: number;
  prix_nuit_enfant: number;
  detail: LigneDetailSemainePersonne[];
  familles?: LigneFamilleSemaine[];
  total_cout_brut?: number;
  total_cout_net?: number;
}
export interface RecapNuitees {
  annee: number; total_a_repartir: number; poids_total: number; prix_par_poids: number;
  personnes: LigneNuitPersonne[]; comptes: LigneNuitCompte[];
  poids_total_avec_tentatifs: number; prix_par_poids_avec_tentatifs: number;
  /** Détail par semaine (règle 1 000 €/semaine). Vide si les paramètres du séjour n'ont pas de nuits. */
  semaines?: SemaineNuitees[];
  /** Budget hebdomadaire (= total_a_repartir / nb_semaines). */
  cout_par_semaine?: number;
  nb_semaines?: number;
  /** Coût moyen d'une nuit enfant (≠ prix_par_poids × 0.8 depuis la règle du report). */
  prix_nuit_enfant?: number;
}

/** Pool de courses d'une semaine (dépenses ÷ repas de la semaine). */
export interface SemaineRepas {
  index: number; libelle: string;
  total_depenses: number; nb_repas: number; prix_par_repas: number;
}
export interface LigneRepasPersonne {
  personne_id: number; prenom: string; compte_id: number; nb_repas: number; montant_du: number;
  nb_repas_semaine?: number[]; du_repas_semaine?: number[];
}
export interface LigneRepasCompte {
  compte_id: number; compte_nom: string; nb_repas_total: number; montant_du: number; depenses_payees: number;
  nb_repas_semaine?: number[]; du_repas_semaine?: number[]; depenses_semaine?: number[];
}
export interface RecapRepas {
  annee: number; total_depenses: number; nb_repas_total: number; prix_par_repas: number;
  personnes: LigneRepasPersonne[]; comptes: LigneRepasCompte[];
  semaines?: SemaineRepas[];
}

export interface LigneSolde {
  compte_id: number; compte_nom: string;
  depenses_payees: number; du_nuitees: number; du_repas: number; total_du: number; solde: number;
  /** Scénario "tous les peut-être ↔ confirmés" */
  du_nuitees_max: number; total_du_max: number; nb_tentatifs: number;
  /** === Séparation maison (à un tiers) / courses (entre familles) === */
  du_maison?: number; du_maison_max?: number; du_maison_semaine?: number[];
  du_courses?: number; du_courses_semaine?: number[]; payee_semaine?: number[];
  /** solde courses = dû courses − payé (>0 la famille doit ; <0 on lui doit) */
  solde_courses?: number; solde_courses_semaine?: number[];
}
export interface RecapSoldes {
  annee: number; total_nuitees: number; total_depenses: number; total_soldes: number;
  lignes: LigneSolde[]; total_nuitees_max: number; semaines_libelles?: string[];
}

export interface Transaction {
  de_compte_id: number; de_compte_nom: string;
  vers_compte_id: number; vers_compte_nom: string;
  montant: number;
}
/** Règlement des courses d'une semaine (soldée indépendamment). */
export interface SemaineReglement {
  index: number; libelle: string;
  total_depenses: number; nb_repas: number; prix_par_repas: number;
  nb_creanciers: number; nb_debiteurs: number; transactions: Transaction[];
}

// === Menus ===
export interface MenuRegimes {
  vege: boolean; vegan: boolean;
  sans_gluten: boolean; sans_lactose: boolean;
  sans_sel: boolean; equilibre: boolean;
  fruits_mer: boolean; arachide: boolean; alcool: boolean;
}
export interface MenuIngredient { nom: string; quantite: number; unite: string }
export interface MenuSuggestion {
  id: string;
  nom: string;
  description: string;
  kid_friendly: boolean;
  tags: string[];
  prix_par_adulte: number;
  prix_total_estime: number;
  /** true si un objectif de coût est fixé et que ce menu est dedans */
  dans_budget?: boolean | null;
  /** true = menu figé (choisi/déposé manuellement par l'utilisateur) */
  fige?: boolean;
  regimes?: MenuRegimes | null;
  ingredients?: MenuIngredient[];
  /** true = menu créé par l'utilisateur (« mon propre menu ») */
  perso?: boolean;
  /** Étapes de la recette complète du menu */
  recette?: string[];
}
export interface IngredientPerso { nom: string; qte: number; unite: string }
export interface MenuPersoOut {
  id: string;                 // "perso-{n}"
  nom: string;
  description: string;
  moment: string;             // M | S | MS
  prix_par_adulte: number;
  ingredients_par_adulte: IngredientPerso[];
}
export interface MenuMeteo {
  tmax: number; tmin: number; precip: number;
  code: number; label: string; emoji: string;
}
export interface MenuRepasJour {
  repas_id: number;
  nb_adulte_ado: number;
  nb_enfant: number;
  nb_bebe: number;
  suggestion: MenuSuggestion | null;
  alternatives: MenuSuggestion[];
  /** true = des convives mais aucune recette du catalogue ne satisfait les préférences */
  aucun_compatible?: boolean;
  /** Prefs propres à CE repas (ajoutées en plus de séjour + jour) */
  prefs_repas?: string[];
  /** Prefs héritées (séjour ∪ jour), pour affichage du contexte */
  prefs_heritees?: string[];
  /** Personne(s) en charge de ce repas (répartition de la gestion) */
  responsables?: MenuResponsable[];
}
export interface MenuResponsable { personne_id: number; prenom: string; avatar_version?: number }
export interface MenuJour {
  date: string;                    // ISO YYYY-MM-DD
  meteo: MenuMeteo | null;         // null si hors fenêtre de prévision
  meteo_tag: string;               // chaud | doux | frais | pluie | inconnue
  prefs_jour: string[];
  repas: { M?: MenuRepasJour; S?: MenuRepasJour };
}
export interface PrefDisponible { key: string; label: string; icon: string; type: 'hard' | 'soft' }
export interface MenuCatalogueItem {
  id: string; nom: string; description: string; moment: string;
  prix_par_adulte: number; kid_friendly: boolean; tags: string[];
  regimes: MenuRegimes;
}
export interface MenusResp {
  annee: number; jours: MenuJour[];
  objectif_cout_repas?: number | null;
  prefs_sejour?: string[];
  prefs_disponibles?: PrefDisponible[];
  catalogue?: MenuCatalogueItem[];
  menus_persos?: MenuPersoOut[];
}

export interface ReglagesMenus {
  objectif_cout_repas: number | null;
  adresse_maison: string;
  rayon_recherche_km: number;
  maison_lat: number | null;
  maison_lon: number | null;
  geocodee: boolean;
  prefs_sejour?: string[];
}

export interface Commerce {
  osm_id?: string;          // identifiant OSM stable (favoris)
  nom: string;
  enseigne: string | null;
  type: string;             // Supérette | Supermarché | Hypermarché
  distance_km: number;
  adresse: string;
  horaires: string | null;
  site_web: string | null;
  lat: number; lon: number;
  itineraire: string;
  favori?: boolean;
}
export interface CommercesResp {
  adresse: string;
  rayon_km: number;
  geocodee: boolean;
  commerces: Commerce[];
}

export interface AdresseSuggestion { label: string; lat: number; lon: number }

export interface TrajetInfo { duree_min: number; distance_km: number }
export interface ItineraireResp {
  voiture: TrajetInfo | null;
  pied: TrajetInfo | null;
  transit_url: string;
  voiture_url: string;
  pied_url: string;
}

export interface RecapReglements {
  annee: number;
  total_depenses_courses: number;
  nb_creanciers: number;
  nb_debiteurs: number;
  transactions: Transaction[];
  /** Règlements séparés par semaine (S1 ≠ S2). */
  semaines?: SemaineReglement[];
}

export type TypeChambre = 'double' | 'solo' | 'enfants'

export type TypeLit = 'lit_simple' | 'lit_double' | 'lit_superpose'

export interface Lit {
  id: number; chambre_id: number; type: TypeLit; ordre: number;
}

/** Nombre de personnes par type de lit. Doit rester en phase avec
 *  app/models/chambre.py::LIT_SPOTS côté backend. */
export const LIT_SPOTS: Record<TypeLit, number> = {
  lit_simple: 1,
  lit_double: 2,
  lit_superpose: 2,
}

export const LIT_LABEL: Record<TypeLit, string> = {
  lit_simple: 'Lit simple',
  lit_double: 'Lit double',
  lit_superpose: 'Lit superposé',
}

export const LIT_ICON: Record<TypeLit, string> = {
  lit_simple: '🛏️',
  lit_double: '🛌',
  lit_superpose: '🪜',
}

export interface Chambre {
  id: number; code: string; etage: number; nom: string;
  type: TypeChambre; capacite: number; description: string; ordre: number;
  /** Liste des lits physiques de la chambre. capacite = Σ LIT_SPOTS[lit.type]. */
  lits: Lit[];
}

export interface Couple {
  id: number; personne_a_id: number; personne_b_id: number;
  chambre_preferee_id: number | null;
}

export interface Assignation {
  personne_id: number; nuit_id: number; chambre_id: number | null;
}

export interface AlerteCouchage {
  type: 'couple_separe' | 'mix_adulte_enfant' | 'sur_capacite' | 'non_assigne';
  severity: 'warning' | 'error';
  message: string;
  personne_ids: number[];
  chambre_id: number | null;
  nuit_id: number | null;
  nuit_libelle: string | null;
}
